import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { useProject } from '../contexts/ProjectContext';

const EVENT_CATEGORIES = ['general', 'maintenance', 'stripping', 'incident', 'chemical', 'observation'];

const CAT_COLORS = {
  maintenance: { bg: '#e8f0fe', color: '#3E68B2' },
  stripping:   { bg: '#e6f4ea', color: '#1e7e34' },
  incident:    { bg: '#fce8e6', color: '#c5221f' },
  chemical:    { bg: '#fef7e0', color: '#b06000' },
  observation: { bg: '#f3e8fd', color: '#7b2d9e' },
  general:     { bg: '#f1f3f4', color: '#5f6368' },
};

// ── Field Events ───────────────────────────────────────────────────────────────

function FieldEventsPanel({ projects }) {
  const [projectId, setProjectId] = useState(null);
  const [events, setEvents]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [editId, setEditId]       = useState(null);
  const [editForm, setEditForm]   = useState({});
  const [form, setForm]           = useState({ event_at: '', category: 'general', description: '' });

  useEffect(() => {
    if (projects.length > 0 && projectId === null) setProjectId(projects[0].id);
  }, [projects]); // eslint-disable-line

  const loadEvents = useCallback(async (pid) => {
    if (!pid) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/projects/${pid}/events`);
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch { setEvents([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadEvents(projectId); }, [projectId, loadEvents]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.event_at || !form.description.trim() || !projectId) return;
    setSaving(true);
    try {
      await api.post(`/api/projects/${projectId}/events`, form);
      setForm({ event_at: '', category: 'general', description: '' });
      loadEvents(projectId);
    } catch {}
    finally { setSaving(false); }
  };

  const saveEdit = async (id) => {
    setSaving(true);
    try {
      await api.patch(`/api/projects/${projectId}/events/${id}`, editForm);
      setEditId(null);
      loadEvents(projectId);
    } catch {}
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await api.delete(`/api/projects/${projectId}/events/${id}`);
      loadEvents(projectId);
    } catch {}
  };

  const cs = (cat) => CAT_COLORS[cat] || CAT_COLORS.general;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <span style={{ fontSize: 13, color: '#6b7a8d' }}>Project:</span>
        <select
          value={projectId || ''}
          onChange={e => { setProjectId(Number(e.target.value)); setEditId(null); }}
          style={{ padding: '5px 10px', fontSize: 13, borderRadius: 6, border: '1px solid #dce3ec' }}
        >
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      <form onSubmit={add} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18, alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: 11, color: '#6b7a8d', marginBottom: 3 }}>Date &amp; time</div>
          <input type="datetime-local" value={form.event_at}
            onChange={e => setForm(f => ({ ...f, event_at: e.target.value }))} required
            style={{ padding: '6px 10px', fontSize: 13, borderRadius: 6, border: '1px solid #dce3ec' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#6b7a8d', marginBottom: 3 }}>Category</div>
          <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
            style={{ padding: '6px 10px', fontSize: 13, borderRadius: 6, border: '1px solid #dce3ec' }}>
            {EVENT_CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 11, color: '#6b7a8d', marginBottom: 3 }}>Description</div>
          <input type="text" value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="What happened?" required
            style={{ width: '100%', padding: '6px 10px', fontSize: 13, borderRadius: 6, border: '1px solid #dce3ec', boxSizing: 'border-box' }} />
        </div>
        <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !projectId}>
          Add Event
        </button>
      </form>

      {loading && <div style={{ color: '#6b7a8d', fontSize: 13 }}>Loading…</div>}
      {!loading && events.length === 0 && (
        <div style={{ color: '#6b7a8d', fontSize: 13, padding: '12px 0' }}>No events logged yet for this project.</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {events.map(ev => editId === ev.id ? (
          <div key={ev.id} style={{ background: '#f8fafc', border: '1px solid #dce3ec', borderRadius: 8, padding: '10px 12px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <input type="datetime-local" value={(editForm.event_at || '').slice(0, 16)}
              onChange={e => setEditForm(f => ({ ...f, event_at: e.target.value }))}
              style={{ padding: '5px 8px', fontSize: 13, borderRadius: 6, border: '1px solid #dce3ec' }} />
            <select value={editForm.category || 'general'} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))}
              style={{ padding: '5px 8px', fontSize: 13, borderRadius: 6, border: '1px solid #dce3ec' }}>
              {EVENT_CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
            </select>
            <input type="text" value={editForm.description || ''}
              onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
              style={{ flex: 1, minWidth: 200, padding: '5px 8px', fontSize: 13, borderRadius: 6, border: '1px solid #dce3ec' }} />
            <button className="btn btn-primary btn-sm" onClick={() => saveEdit(ev.id)} disabled={saving}>Save</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditId(null)}>Cancel</button>
          </div>
        ) : (
          <div key={ev.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px', background: '#fff', border: '1px solid #eaeff5', borderRadius: 8 }}>
            <span style={{ fontSize: 11, color: '#6b7a8d', whiteSpace: 'nowrap', marginTop: 2, minWidth: 120 }}>
              {(ev.event_at || '').slice(0, 16).replace('T', ' ')}
            </span>
            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: cs(ev.category).bg, color: cs(ev.category).color, whiteSpace: 'nowrap' }}>
              {ev.category}
            </span>
            <span style={{ flex: 1, fontSize: 13, color: '#2c3e50', lineHeight: 1.5 }}>{ev.description}</span>
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                onClick={() => { setEditId(ev.id); setEditForm({ event_at: (ev.event_at || '').slice(0, 16), category: ev.category, description: ev.description }); }}>
                Edit
              </button>
              <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: '#dc3545' }} onClick={() => remove(ev.id)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Project Instructions ───────────────────────────────────────────────────────

function ProjectInstructionsPanel({ projects }) {
  const [projectId, setProjectId]   = useState(null);
  const [text, setText]             = useState('');
  const [original, setOriginal]     = useState('');
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [autoNotes, setAutoNotes]   = useState([]);

  useEffect(() => {
    if (projects.length > 0 && projectId === null) setProjectId(projects[0].id);
  }, [projects]); // eslint-disable-line

  const load = useCallback(async (pid) => {
    if (!pid) return;
    setLoading(true);
    try {
      const [instrRes, notesRes] = await Promise.all([
        api.get(`/api/projects/${pid}/instructions`),
        api.get(`/api/projects/${pid}/notes`),
      ]);
      const val = instrRes.data?.instructions || '';
      setText(val);
      setOriginal(val);
      const notes = Array.isArray(notesRes.data) ? notesRes.data : [];
      setAutoNotes(notes.filter(n => n.source === 'chat'));
    } catch { setText(''); setOriginal(''); setAutoNotes([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(projectId); }, [projectId, load]);

  const save = async () => {
    if (!projectId) return;
    setSaving(true);
    try {
      await api.put(`/api/projects/${projectId}/instructions`, { instructions: text });
      setOriginal(text);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
    finally { setSaving(false); }
  };

  const deleteNote = async (id) => {
    try {
      await api.delete(`/api/projects/${projectId}/notes/${id}`);
      setAutoNotes(n => n.filter(x => x.id !== id));
    } catch {}
  };

  const dirty = text !== original;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <span style={{ fontSize: 13, color: '#6b7a8d' }}>Project:</span>
        <select value={projectId || ''} onChange={e => setProjectId(Number(e.target.value))}
          style={{ padding: '5px 10px', fontSize: 13, borderRadius: 6, border: '1px solid #dce3ec' }}>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ color: '#6b7a8d', fontSize: 13 }}>Loading…</div>
      ) : (
        <>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={`Enter project-specific instructions for Claude.\n\nExamples:\n- Target copper concentration: 45 g/L\n- Electrolyte: CuSO₄ + H₂SO₄, pH 1.8–2.2\n- Stack has 16 cells in series\n- Normal batch duration: 48–72 hours\n- Stripping done manually every Monday\n- Cell 7 has a known busbar issue since March 2026`}
            rows={14}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '10px 12px', fontSize: 13, lineHeight: 1.7,
              borderRadius: 8, border: '1px solid #dce3ec',
              fontFamily: 'inherit', resize: 'vertical', outline: 'none',
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !dirty}>
              {saving ? 'Saving…' : 'Save Instructions'}
            </button>
            {saved && <span style={{ fontSize: 12, color: '#1e7e34' }}>✓ Saved</span>}
            {dirty && !saving && <span style={{ fontSize: 12, color: '#b06000' }}>Unsaved changes</span>}
          </div>

          {autoNotes.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7a8d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Auto-extracted from AI chat
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {autoNotes.map(n => (
                  <div key={n.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '6px 10px', background: '#f5f7fa', borderRadius: 6, border: '1px solid #eaeff5' }}>
                    <span style={{ flex: 1, fontSize: 12, color: '#4a5568', lineHeight: 1.5 }}>{n.content}</span>
                    <span style={{ fontSize: 11, color: '#a0aab4', whiteSpace: 'nowrap' }}>{(n.created_at || '').slice(0, 10)}</span>
                    <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: '#dc3545', padding: '1px 6px' }} onClick={() => deleteNote(n.id)}>×</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Portal Instructions ────────────────────────────────────────────────────────

function PortalInstructionsPanel() {
  const [text, setText]             = useState('');
  const [original, setOriginal]     = useState('');
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshDone, setRefreshDone] = useState(null); // null | number (project count)

  useEffect(() => {
    setLoading(true);
    api.get('/api/portal/instructions')
      .then(res => {
        const val = res.data?.instructions || '';
        setText(val); setOriginal(val);
      })
      .catch(() => { setText(''); setOriginal(''); })
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/portal/instructions', { instructions: text });
      setOriginal(text);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
    finally { setSaving(false); }
  };

  const refreshAll = async () => {
    setRefreshing(true);
    setRefreshDone(null);
    try {
      const res = await api.post('/api/insights/refresh-all');
      const n = res.data?.project_count ?? 0;
      setRefreshDone(n);
      setTimeout(() => setRefreshDone(null), 4000);
    } catch {}
    finally { setRefreshing(false); }
  };

  const dirty = text !== original;

  return (
    <div>
      {loading ? (
        <div style={{ color: '#6b7a8d', fontSize: 13 }}>Loading…</div>
      ) : (
        <>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={`Enter technical rules and domain knowledge that Claude should apply across all projects.\n\nExamples:\n- Series circuit law: low-V cell = short circuit (dendrite/particle), high-V cell = resistance fault (scale/fouling)\n- Never mix idle and running readings when computing trends\n- Voltage redistribution: ΔV_per_cell = V_shorted / (N-1)\n- Normal within-batch voltage rise: 0.3–0.8V from start to harvest`}
            rows={18}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '10px 12px', fontSize: 13, lineHeight: 1.7,
              borderRadius: 8, border: '1px solid #dce3ec',
              fontFamily: 'inherit', resize: 'vertical', outline: 'none',
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !dirty}>
              {saving ? 'Saving…' : 'Save Instructions'}
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={refreshAll}
              disabled={refreshing}
              title="Regenerate AI Insights for all projects using the latest instructions"
            >
              {refreshing ? '⏳ Regenerating…' : '↻ Refresh AI Insights for all projects'}
            </button>
            {saved && <span style={{ fontSize: 12, color: '#1e7e34' }}>✓ Saved</span>}
            {dirty && !saving && <span style={{ fontSize: 12, color: '#b06000' }}>Unsaved changes</span>}
            {refreshDone !== null && (
              <span style={{ fontSize: 12, color: '#1e7e34' }}>
                ✓ Regenerating insights for {refreshDone} project{refreshDone !== 1 ? 's' : ''} in background
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Technical Library ──────────────────────────────────────────────────────────

function TechnicalLibraryPanel() {
  const [docs, setDocs]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [title, setTitle]         = useState('');
  const [file, setFile]           = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState('');

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/knowledge-docs');
      setDocs(Array.isArray(res.data) ? res.data : []);
    } catch { setDocs([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !title.trim()) return;
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('file', file);
      await api.post('/api/knowledge-docs', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setTitle(''); setFile(null);
      e.target.reset();
      loadDocs();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id, docTitle) => {
    if (!window.confirm(`Delete "${docTitle}"?`)) return;
    try { await api.delete(`/api/knowledge-docs/${id}`); loadDocs(); } catch {}
  };

  return (
    <div>
      <div style={{ fontSize: 13, color: '#6b7a8d', marginBottom: 14 }}>
        Supported formats: <strong>PDF, TXT, MD</strong> · Max 15,000 characters per document.
      </div>
      {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
      <form onSubmit={upload} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, color: '#6b7a8d', marginBottom: 3 }}>Document title</div>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)}
            placeholder="e.g. emew Operating Manual v3" required
            style={{ padding: '6px 10px', fontSize: 13, borderRadius: 6, border: '1px solid #dce3ec', width: 280 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#6b7a8d', marginBottom: 3 }}>File</div>
          <input type="file" accept=".pdf,.txt,.md" required onChange={e => setFile(e.target.files[0])} style={{ fontSize: 13 }} />
        </div>
        <button type="submit" className="btn btn-primary btn-sm" disabled={uploading || !title.trim() || !file}>
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
      </form>
      {loading && <div style={{ color: '#6b7a8d', fontSize: 13 }}>Loading…</div>}
      {!loading && docs.length === 0 && (
        <div style={{ color: '#6b7a8d', fontSize: 13, padding: '12px 0' }}>No documents uploaded yet.</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {docs.map(d => (
          <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#fff', border: '1px solid #eaeff5', borderRadius: 8 }}>
            <span style={{ fontSize: 16 }}>📄</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#142D55' }}>{d.title}</div>
              <div style={{ fontSize: 11, color: '#6b7a8d', marginTop: 2 }}>
                {d.original_filename} · {((d.char_count || 0) / 1000).toFixed(1)}k chars · {(d.uploaded_at || '').slice(0, 10)}
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: '#dc3545' }} onClick={() => remove(d.id, d.title)}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────

const SUB_TABS = [
  { key: 'portal',   label: '🌐 Portal Instructions' },
  { key: 'project',  label: '📝 Project Instructions' },
  { key: 'events',   label: '📋 Field Events' },
  { key: 'library',  label: '📚 Technical Library' },
];

const TAB_DESCRIPTIONS = {
  portal: {
    title: 'Portal Instructions',
    what: 'Global technical rules and domain knowledge that apply to all projects on this platform.',
    usage: 'Included in every Claude analysis and chat session across all projects. Use this for fundamental physics, circuit laws, process rules, and analytical guidelines that Claude should always apply — regardless of which project is being analysed.',
  },
  project: {
    title: 'Project Instructions',
    what: 'Project-specific context: target parameters, electrolyte chemistry, equipment configuration, operating norms, and known site-specific details.',
    usage: 'Included in Claude analyses and chat sessions for this project only. The more specific and accurate these instructions are, the better Claude\'s analysis will be for this project.',
  },
  events: {
    title: 'Field Events Log',
    what: 'A timestamped record of real-world events — maintenance, incidents, chemical additions, stripping cycles, or anything that happened on-site.',
    usage: 'Used by Claude Analytics for this project only. Claude cross-references these events with data to explain anomalies and correlate performance shifts with actual field activity.',
  },
  library: {
    title: 'Technical Knowledge Library',
    what: 'Technical reference documents — operating manuals, research papers, SOPs, chemistry references — that are not project-specific.',
    usage: 'Used by Claude Analytics across all projects. Text is extracted from uploaded documents and injected into every analysis and chat session, giving Claude a foundation of technical knowledge to reason from.',
  },
};

export default function KnowledgeSection() {
  const { projects } = useProject();
  const safeProjects = Array.isArray(projects) ? projects : [];
  const [sub, setSub] = useState('portal');

  const desc = TAB_DESCRIPTIONS[sub];

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' }}>
        {SUB_TABS.map(t => (
          <button key={t.key} className={`btn btn-sm ${sub === t.key ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setSub(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab description */}
      <div style={{ background: '#f5f7fa', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px', marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#142D55', marginBottom: 6 }}>{desc.title}</div>
        <div style={{ fontSize: 13, color: '#4a5568', lineHeight: 1.6, marginBottom: 8 }}>{desc.what}</div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#eaf1fb', borderRadius: 6, padding: '8px 12px' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#3E68B2', whiteSpace: 'nowrap', marginTop: 1 }}>Used by Claude</span>
          <span style={{ fontSize: 13, color: '#2c4a7a', lineHeight: 1.5 }}>{desc.usage}</span>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {sub === 'portal'  && <PortalInstructionsPanel />}
          {sub === 'project' && <ProjectInstructionsPanel projects={safeProjects} />}
          {sub === 'events'  && <FieldEventsPanel projects={safeProjects} />}
          {sub === 'library' && <TechnicalLibraryPanel />}
        </div>
      </div>
    </div>
  );
}
